// components/Orders/OrderMoneyPanel.jsx
// ─────────────────────────────────────────────────────────────────────────────
// The money on an order, printed so the visible lines add up to the printed
// total. Figures come from utilities/orderSummary.js — never re-derived here.
//
// The screens this replaces showed "Cart Total 70,000", a blank "Shipping
// Fees", and "Total Order Cost 76,500": both ends of an arithmetic statement
// with a wrong middle, on the panel whose whole job is to explain a charge. The
// 6,500 lived in `deliveryFee`, which no screen rendered.
//
// The "Other charges" row is deliberate. When the parts do not explain the
// total, the gap is NAMED rather than swallowed, so the next divergence
// announces itself instead of hiding for three months.
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import { buildOrderSummary, formatAmount } from "../../utilities/orderSummary";

const Row = ({ label, value, className = "", title }) => (
  <div className={`rod-money-row ${className}`} title={title}>
    <span className="rod-money-label">{label}</span>
    <span className="rod-money-value">{value}</span>
  </div>
);

const OrderMoneyPanel = ({ order, currency = "Ush" }) => {
  const s = buildOrderSummary(order);
  const amount = (n) => `${currency} ${formatAmount(n)}`;

  return (
    <div>
      <Row label={`Items (${s.itemsLabel})`} value={amount(s.itemsPrice)} />

      <Row
        label="Delivery fee"
        value={s.deliveryFee > 0 ? amount(s.deliveryFee) : "Free"}
        title={
          s.deliveryFeeIsLegacy
            ? "Priced by the old flat zone fee, before the fee engine"
            : "Server-computed from the signed quote at checkout"
        }
      />

      {s.processingFee > 0 && (
        <Row label={s.processingLabel} value={amount(s.processingFee)} />
      )}

      {s.adjustment !== 0 && (
        <Row
          className={s.adjustment > 0 ? "rod-money-flag" : ""}
          label={s.adjustment > 0 ? "Other charges" : "Discount"}
          value={amount(Math.abs(s.adjustment))}
          title={
            s.adjustment > 0
              ? "The lines above do not account for this part of the total"
              : undefined
          }
        />
      )}

      <Row
        className="rod-money-total"
        label="Total charged"
        value={amount(s.totalPrice)}
      />

      {s.taxPrice > 0 && (
        <div className="rod-money-note">
          Includes VAT of {amount(s.taxPrice)}. Prices carry tax inside them, so
          this is contained in the total above, not added to it.
        </div>
      )}

      {s.adjustment > 0 && (
        <div className="rod-money-note" style={{ color: "#a4291d" }}>
          {amount(s.adjustment)} of this total is not explained by the lines
          above. Check the order before collecting.
        </div>
      )}
    </div>
  );
};

export default OrderMoneyPanel;
